import React from 'react';
import {useTranslation} from 'react-i18next';
import {
    NS,
    chargeCrossesRetentionChange,
    formatAmount,
    formatLongDate,
    howYouPayKey,
    isAutomaticMethod,
    nextChargeAmountOf,
    nextChargeOf,
    providerName,
    retentionAmountText,
    subscriptionPillOf,
} from './subscriptionPageModel';
import {LinkButton, Pill} from './SubscriptionParts';

const RAW = {escapeValue: false};
const PILL_TONE = {active: 'ok', warning: 'warn', cancelled: 'bad'};

/**
 * «Tu plan» (mockup 1, arriba): el nombre, lo que paga ESTA agencia y cómo le cobran.
 *
 * El precio es sólo `price` (el convenio): sin él, el nombre del plan solo. Nunca la lista (cc#208).
 *
 * Con un descuento de retención que el convenio confirma (`retention`, UD-17 M1), la línea del descuento
 * reemplaza «mantienes este precio…»: durante el descuento, ese precio NO se mantiene.
 */
export function PlanSummaryCard({planName, isCustom, price, annual, methodKey, paymentSubscription,
                                    currentPeriod, billingReady, onChangePlan, onAnnual, retention = null}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const subscription = paymentSubscription && paymentSubscription.data;
    const pill = isAutomaticMethod(methodKey) ? subscriptionPillOf(subscription) : null;

    return (
        <section className="sp-card sp-plan" aria-labelledby="sp-plan-title">
            <div className="sp-plan__head">
                <div>
                    <p className="sp-eyebrow">{t(`${NS}.plan.eyebrow`)}</p>
                    <div className="sp-plan__name-row">
                        <h2 id="sp-plan-title" className="sp-plan__name">{planName}</h2>
                        <Pill tone="ok">{t(`${NS}.plan.active`)}</Pill>
                    </div>
                </div>
                {price && (
                    <div className="sp-plan__price" data-testid="sp-plan-price">
                        <div className="sp-plan__amount">
                            {formatAmount(price.monthlyEquivalent, price.currency, lang)}
                            {' '}
                            <span className="sp-plan__per">
                                {t(`${NS}.plan.per_month`)}
                                {price.plusVat && ` ${t(`${NS}.plan.plus_vat`)}`}
                            </span>
                        </div>
                        <p className="sp-plan__keep">
                            {price.period && `${t(`${NS}.plan.period_${price.period}`)} · `}
                            {retention
                                ? <RetentionLine retention={retention} plusVat={price.plusVat} lang={lang}/>
                                : t(`${NS}.plan.keep_price`)}
                        </p>
                        {retention && retention.state === 'active' && (
                            <p className="sp-plan__keep" data-testid="sp-retention-back">
                                {t(`${NS}.retention.back_to_full`, {
                                    date: formatLongDate(retention.backToFullOn, lang),
                                    amount: retentionAmountText(t, retention.fullAmount, retention.currency,
                                        price.plusVat, lang),
                                    interpolation: RAW,
                                })}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {billingReady && (
                <PlanFacts methodKey={methodKey} price={price} subscription={subscription} pill={pill}
                           currentPeriod={currentPeriod} lang={lang} retention={retention}/>
            )}

            {pill === 'warning' && (
                <p className="sp-plan__warning" role="note">
                    {t(`${NS}.plan.sub_warning_help`, {provider: providerName(subscription.provider), interpolation: RAW})}
                </p>
            )}

            <p className="sp-plan__includes">
                <span dangerouslySetInnerHTML={{__html: t(`${NS}.plan.includes`)}}/>
                {' '}
                {!isCustom && <LinkButton onClick={onChangePlan}>{t(`${NS}.plan.see_all`)}</LinkButton>}
            </p>

            {isCustom ? (
                <p className="sp-plan__custom">{t(`${NS}.plan.custom`)}</p>
            ) : (
                <div className="sp-actions">
                    <button type="button" className="btn -alt" onClick={onChangePlan}>
                        {t(`${NS}.plan.change_plan`)}
                    </button>
                    {annual && (
                        <button type="button" className="btn -alt -negative" onClick={onAnnual}>
                            {t(`${NS}.plan.go_annual`)}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

/** La línea del descuento: antes de empezar, cuándo y cuánto; en curso, el porcentaje y hasta cuándo. */
function RetentionLine({retention, plusVat, lang}) {
    const {t} = useTranslation();
    if (retention.state === 'upcoming') {
        return (
            <span data-testid="sp-retention">
                {t(`${NS}.retention.upcoming`, {
                    start: formatLongDate(retention.startsOn, lang),
                    amount: retentionAmountText(t, retention.amount, retention.currency, plusVat, lang),
                    end: formatLongDate(retention.endsOn, lang),
                    interpolation: RAW,
                })}
            </span>
        );
    }
    return (
        <span data-testid="sp-retention">
            {t(`${NS}.retention.active`, {
                percent: retention.percent,
                end: formatLongDate(retention.endsOn, lang),
                interpolation: RAW,
            })}
        </span>
    );
}

/**
 * Las tres celdas de abajo: cuándo le cobran (o cómo le llega la factura), la forma de pago y el período.
 * Una celda sin dato no se dibuja: con el pedido de la suscripción caído no hay «Próximo cobro».
 */
function PlanFacts({methodKey, price, subscription, pill, currentPeriod, lang, retention}) {
    const {t} = useTranslation();
    const isAutomatic = isAutomaticMethod(methodKey);
    const next = isAutomatic ? nextChargeOf(subscription) : null;
    const howKey = isAutomatic ? null : howYouPayKey(methodKey);
    // El monto, sólo si el último cobro del proveedor no lo contradice (C-sp-fe-02), ni el cobro cae en el
    // cambio de precio de un descuento de retención (UD-17): ahí el precio de hoy ya no es el de ese cobro.
    const nextAmount = next && !chargeCrossesRetentionChange(retention, next.date)
        ? nextChargeAmountOf(price, subscription)
        : null;

    return (
        <dl className="sp-facts">
            {next && (
                <div className="sp-fact" data-testid="sp-next-charge">
                    <dt>{t(`${NS}.plan.next_charge`)}</dt>
                    <dd>
                        {nextAmount !== null
                            ? t(`${NS}.plan.next_charge_value`, {
                                date: formatLongDate(next.date, lang),
                                price: formatAmount(nextAmount, price.currency, lang),
                                interpolation: RAW,
                            })
                            : formatLongDate(next.date, lang)}
                    </dd>
                    <p className="sp-fact__note">{t(`${NS}.plan.auto_${next.provider}`)}</p>
                </div>
            )}
            {howKey && (
                <div className="sp-fact">
                    <dt>{t(`${NS}.plan.how_you_pay`)}</dt>
                    <dd className="sp-fact__text">{t(`${NS}.plan.${howKey}`)}</dd>
                </div>
            )}
            {methodKey && (
                <div className="sp-fact">
                    <dt>{t(`${NS}.plan.payment_method`)}</dt>
                    <dd>{t(`${NS}.method.${methodKey}`)}</dd>
                    {pill && (
                        <div className="sp-fact__pill">
                            <Pill tone={PILL_TONE[pill]}>{t(`${NS}.plan.sub_${pill}`)}</Pill>
                        </div>
                    )}
                </div>
            )}
            {currentPeriod && (
                <div className="sp-fact">
                    <dt>{t(`${NS}.plan.current_period`)}</dt>
                    <dd>{currentPeriod}</dd>
                </div>
            )}
        </dl>
    );
}
